import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { isValidElement, type ElementType, type ReactNode } from 'react'
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'

function renderStatIcon(icon: ReactNode | LucideIcon): ReactNode {
  if (icon == null) {
    return null
  }
  if (isValidElement(icon)) {
    return icon
  }
  if (typeof icon === 'string' || typeof icon === 'number') {
    return (
      <span className="text-xl" aria-hidden="true">
        {icon}
      </span>
    )
  }
  const Comp = icon as ElementType
  return <Comp className="h-4 w-4 text-primary" />
}

interface StatCardProps {
  title: string
  value: string | number
  icon?: ReactNode | LucideIcon
  description?: string
  trend?: {
    value: number
    isPositive: boolean
  }
  className?: string
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger'
}

export const StatCard = ({
  title,
  value,
  icon: Icon,
  description,
  trend,
  className = '',
  variant = 'default',
}: StatCardProps) => {
  const variantStyles = {
    default: 'bg-primary/5 border-transparent',
    primary: 'bg-primary/5 border-transparent',
    success: 'bg-green-50 border-transparent',
    warning: 'bg-amber-50 border-transparent',
    danger: 'bg-red-50 border-transparent',
  }

  return (
    <Card className={cn('transition-all hover:shadow-md border-transparent', variantStyles[variant], className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {Icon && (
          <div className="h-8 w-8 flex items-center justify-center rounded-full bg-primary/10">
            {renderStatIcon(Icon)}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-3xl font-bold tracking-tight">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        )}
        {trend && (
          <div
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
              trend.isPositive
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            )}
          >
            {trend.isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            <span>{Math.abs(trend.value)}%</span>
            <span className="text-muted-foreground/80">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
